import { createContext, useContext } from 'react';
import type { EduTour } from '@/api/types';

/**
 * Tour engine context (C1/C2/C4). Kept separate from the provider component so consumers import a
 * hook without pulling the provider's module graph (and to satisfy react-refresh component-only
 * export). The provider (TourProvider.tsx) owns the AntD Tour driver, the tour cache, the first-run
 * offer, and `edu.*` emission.
 */

export interface StartTourOpts {
  /** Completing this tour posts /v1/edu/first-run/complete (flagship first-run). */
  isFirstRun?: boolean;
  /** Navigate to the tour's surface home before opening (the first-run hop). */
  navigate?: boolean;
}

export interface TourContextValue {
  /** Open a tour from its data definition. Safe to call from any surface. */
  startTour: (tour: EduTour, opts?: StartTourOpts) => void;
  /** Cached fetch of the tours for a surface (empty array when the endpoint is absent). */
  loadTours: (surface: string) => Promise<EduTour[]>;
  /** Re-offer / re-launch the flagship first-run tour (Help menu). */
  relaunchFirstRun: () => Promise<void>;
  /** True while a tour overlay is open. */
  active: boolean;
}

const noop = () => {};

export const TourContext = createContext<TourContextValue>({
  startTour: noop,
  loadTours: async () => [],
  relaunchFirstRun: async () => {},
  active: false,
});

/** Access the tour engine. Returns inert defaults if used outside the provider (never throws). */
export function useTour(): TourContextValue {
  return useContext(TourContext);
}
